// @ts-nocheck
import axios, { AxiosRequestConfig, Method } from 'axios';
// eslint-disable-next-line import/no-extraneous-dependencies
import { createBrowserHistory } from 'history';
import { Auth } from '../storage/StorageKeys';
import StorageService from '../storage/StorageService';
import DateTime from '../utilities/DateTime';
import * as QueryString from 'qs';

interface ApiResponse<TData> {
    code: number;
    data?: TData;
    error?: {
        message?: string;
    };
};

const defaultAxiosConfig: AxiosRequestConfig<any> = {
    method: 'get',
    baseURL: process.env.REACT_APP_API_URL, 
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000,
    responseType: 'json',
    responseEncoding: 'utf8',
    maxContentLength: 4000,
};

const handleDatesinResponse = (response: any) => {
    if (response == null || response === undefined || typeof response !== 'object') {
        return response;
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?$/;

    Object.keys(response).forEach((key) => {
        const value = response[key];
        if (value && typeof value === 'string' && dateRegex.test(value) === true) {
            response[key] = DateTime.getDateFromISOString(value);
        } else if (typeof value === 'object') {
            handleDatesinResponse(value);
        }
    });

    return response;
};

function createAxiosConfig<TRequest>(request: TRequest, token: string | null, config?: AxiosRequestConfig<any>) {
    let serviceConfig = defaultAxiosConfig;

    if (config !== undefined) {
        serviceConfig = {
            ...serviceConfig,
            ...config,
        };
    }

    if (serviceConfig.method === 'get') {
        serviceConfig.params = request;
        serviceConfig.paramsSerializer = (p) => QueryString.stringify(p, { arrayFormat: 'repeat' });
    } else {
        serviceConfig.data = request;
    }

    if (token != null) {
        serviceConfig.headers!.Authorization = `Bearer ${token}`;
    }

    return serviceConfig;
}

async function handleApiError(error: any, redirectToLoginOn401: boolean) {
    const response: ApiResponse<any> = {
        code: 500,
    };
    if (error !== undefined) {
        if (error.status === 400) {
            // Bad Request - Most likely Validation errors
            response.error = {
                message: error.data
            };
        } else if (error.status === 401) {
            // Unauthorized
            await StorageService.remove(Auth.Token);
            response.error = {
                message: 'You are not logged in.',
            };
            if (redirectToLoginOn401 === true) {
                createBrowserHistory().push('/login');
                window.location.reload();
            }
        } else if (error.status === 403) {
            // Forbidden
            response.error = {
                message: 'Access Denied',
            };
        } else {
            // 500 - Unknown
            response.error = {
                message: 'Unable to complete request.',
            };
        }
    }

    return response;
}
async function callService<TRequest, TResponse>(
    url: string,
    method: Method,
    request?: TRequest,
    config?: AxiosRequestConfig<any>
): Promise<ApiResponse<TResponse>> {
    const token = await StorageService.get(Auth.Token);
    const axiosConfig = createAxiosConfig(request, token, {
        url,
        method,
        ...config,
    });

    const client = axios.create();

    client.interceptors.response.use((response) => {
        handleDatesinResponse(response);
        return response;
    });

    try {
        const response = await client.request(axiosConfig);
        return {
            code: response.status,
            data: response.status === 204 ? undefined : response.data,
        };
    } catch (error: any) {
        console.log(error);
        if (error !== undefined) {
            return handleApiError(error.response, true);
        }
        return {
            code: 500,
            error: {
                message: 'Unknown Error',
            },
        };
    }
}

const Api = {
    get: async <TRequest, TResponse>(url: string, params?: TRequest) => {
        const response = await callService<TRequest, TResponse>(url, 'get', params);
        if (response.error !== undefined) {
            throw new Error(response.error.message);
        }
        return response.data;
    },
    post: async <TRequest, TResponse>(url: string, data?: TRequest) => {
        const response = await callService<TRequest, TResponse>(url, 'post', data);
        if (response.error !== undefined) {
            console.log(response.error);
            throw new Error(response.error.message); 
        }
        return response.data;
    },
    put: async <TRequest, TResponse>(url: string, data?: TRequest) => {
        const response = await callService<TRequest, TResponse>(url, 'put', data);
        if (response.error !== undefined) {
            throw new Error(response.error.message); 
        }
        return response.data;
    },

    delete: async <TRequest, TResponse>(url: string, data?: TRequest) => {
        const response = await callService<undefined, void>(url, 'delete', undefined);
        if (response.error !== undefined) {
            throw new Error(response.error.message);
        }
    },
};

export default Api;
